import { DateTime } from "luxon"
import * as yup from "yup"
import {
    DeliveryTimesField,
    DeliveryWithCart,
    DeliveryTimeDay,
    isLegacyExtraServiceOrderLine,
    isProductWishOrderLine,
} from "../../form/formTypes"
import type { AppLocalization } from "../../../../../../modules/localization/appLocalization"
import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import type { SelectedPriceListItem } from "../../../createOrderTypes"
import type { DeadlineMessageParam, DeadlineMessageProductFlow } from "./deliveryApi"
import type { CreateUpdateDeliveryFormValues } from "./CreateUpdateDeliveryTimePopover"

export const MAX_DELIVERIES = 5
export const DAY_OF_WEEK_NUMBER = [1, 2, 3, 4, 5, 6, 7]

type L10n = Pick<AppLocalization, "getString">

type DeliveryTimesSchemaParams = Readonly<{
    l10n: L10n
    dateTimeFactory: DateTimeFactory
    allowedWeekdays: number[]
    closingDates: string[]
    canOverrideClosingDates: boolean
    grandfatheredDate?: DateTime | null
    openingHours: DeliveryTimeDay[]
    allowPastDates: boolean
}>

/** Shared by every form that edits `deliveryDate` + `deliveryTimes[].time`. */
export function buildDeliveryTimesSchema({
    l10n,
    dateTimeFactory,
    allowedWeekdays,
    closingDates,
    canOverrideClosingDates,
    grandfatheredDate,
    openingHours,
    allowPastDates,
}: DeliveryTimesSchemaParams) {
    return yup.object({
        deliveryDate: yup
            .mixed<DateTime>()
            .nullable()
            .required(l10n.getString("required-field"))
            .test(
                "delivery-date-allowed",
                l10n.getString("create-order-delivery-times-dialog-delivery-date-close-tooltip"),
                (value) =>
                    validateDeliveryDate(
                        value,
                        { allowedWeekdays, closingDates, canOverrideClosingDates, grandfatheredDate },
                        dateTimeFactory,
                    ).isSelectable,
            ),

        deliveryTimes: yup
            .array()
            .of(
                yup.object({
                    time: yup
                        .mixed<DateTime>()
                        .nullable()
                        .required(l10n.getString("required-field"))
                        .test(
                            "within-opening-hours",
                            l10n.getString("create-order-delivery-times-opening-hours-error"),
                            function (value) {
                                const parent = this.from?.[1]?.value as CreateUpdateDeliveryFormValues
                                const deliveryDate = parent?.deliveryDate

                                if (!deliveryDate || !value) return true

                                return isWithinOpeningHours(value, deliveryDate, openingHours, dateTimeFactory)
                            },
                        )
                        .test(
                            "not-in-past-if-today",
                            l10n.getString("create-order-delivery-times-past-time-error"),
                            function (value) {
                                if (allowPastDates) return true

                                const parent = this.from?.[1]?.value as CreateUpdateDeliveryFormValues
                                const deliveryDate = parent?.deliveryDate
                                if (!deliveryDate || !value) return true

                                return isNotInPastIfToday(value, deliveryDate, dateTimeFactory)
                            },
                        ),
                }),
            )
            .test(
                "unique-times",
                l10n.getString("create-order-delivery-times-same-time-error"),
                function (deliveryTimes) {
                    if (!deliveryTimes || deliveryTimes.length === 0) return true

                    const formatted = deliveryTimes.map((t) => (t.time ? dateTimeFactory.toHourMinute(t.time) : null))
                    const seen = new Set()

                    for (let i = 0; i < formatted.length; i++) {
                        if (formatted[i] && seen.has(formatted[i])) {
                            return this.createError({
                                path: `deliveryTimes[${i}].time`,
                                message: l10n.getString("create-order-delivery-times-same-time-error"),
                            })
                        }
                        seen.add(formatted[i]!)
                    }

                    return true
                },
            ),
    })
}

export const prepareInitialValues = (
    dateTimeFactory: DateTimeFactory,
    eventStartTime: DateTime | null,
    openingHours: DeliveryTimeDay[],
    deliveries?: DeliveryTimesField,
): CreateUpdateDeliveryFormValues => {
    const deliveryDate = deliveries?.deliveryDate ? dateTimeFactory.fromISO(deliveries.deliveryDate) : eventStartTime

    const limits = deliveryDate ? getOpeningLimitsForDate(deliveryDate, openingHours, dateTimeFactory) : null

    if (!deliveries || deliveries.deliveryTimes.length === 0) {
        let initialTime: DateTime | null = eventStartTime

        if (eventStartTime && deliveryDate && limits) {
            const minBound = deliveryDate.set({ hour: limits.minTime.hour, minute: limits.minTime.minute })
            const maxBound = deliveryDate.set({ hour: limits.maxTime.hour, minute: limits.maxTime.minute })
            const isInsideBounds = eventStartTime >= minBound && eventStartTime <= maxBound

            initialTime = isInsideBounds ? eventStartTime : null
        }

        return {
            deliveryDate,
            deliveryTimes: [{ time: initialTime, orderLines: [] }],
        }
    }

    const baseDateISO = deliveries?.deliveryDate
        ? deliveries.deliveryDate // "yyyy-MM-dd"
        : deliveryDate
          ? deliveryDate.toISODate()! //  "yyyy-MM-dd"
          : dateTimeFactory.now().toISODate()!

    const deliveryTimes = sortedDeliveryTimes(deliveries.deliveryTimes).map((delivery) => {
        const timeString = delivery.time ? `${baseDateISO}T${delivery.time}` : null

        return {
            time: timeString ? dateTimeFactory.fromISO(timeString) : null,
            orderLines: delivery.orderLines || [],
        }
    })

    return { deliveryDate, deliveryTimes }
}

type ViewPickerType = "hours" | "minutes" | "seconds"

export function generateDisabledTimeSlots(dateTimeFactory: DateTimeFactory, otherTimes: Array<string | null>) {
    const disabled = new Set(otherTimes.filter((t): t is string => Boolean(t)))

    return (value: DateTime, view: ViewPickerType): boolean =>
        view === "minutes" && value?.isValid && disabled.has(dateTimeFactory.toHourMinute(value))
}

/** The shop's configured delivery window for a given date — never narrowed by "now". */
export type DeliveryOpeningLimits = Readonly<{
    minTime: DateTime
    maxTime: DateTime
}>

export function getOpeningLimitsForDate(
    date: DateTime,
    openingHours: DeliveryTimeDay[],
    dateTimeFactory: DateTimeFactory,
): DeliveryOpeningLimits | null {
    if (!openingHours) return null

    const targetWeekday = date.weekday // 1–7
    const matched = openingHours.find((o) => o.dayOfWeek === targetWeekday)

    if (!matched) return null

    return {
        minTime: dateTimeFactory.fromHourMinute(matched.openingTime).set({
            year: date.year,
            month: date.month,
            day: date.day,
        }),
        maxTime: dateTimeFactory.fromHourMinute(matched.closingTime).set({
            year: date.year,
            month: date.month,
            day: date.day,
        }),
    }
}

// Delivery order must not depend on the API response order: sort every value
// before it is written into the form, and keep entries without a time last.
export function sortedDeliveryTimes(times: DeliveryWithCart[]): DeliveryWithCart[] {
    return [...times].sort((a, b) => {
        if (!a.time) return b.time ? 1 : 0
        if (!b.time) return -1
        return a.time.localeCompare(b.time)
    })
}

export function earliestDeliveryTime(times: DeliveryWithCart[]): string | null {
    return sortedDeliveryTimes(times)[0]?.time || null
}

export type DeliveryDateRules = Readonly<{
    allowedWeekdays: number[]
    closingDates: string[]
    canOverrideClosingDates: boolean
    // The date the order was loaded with: still selectable, whatever made it invalid since.
    grandfatheredDate?: DateTime | null
}>

export type DeliveryDateValidationResult = Readonly<{
    isAllowedWeekday: boolean
    isNotClosed: boolean
    isValidDeliveryDate: boolean
    // "valid" says the shop is open; "selectable" also lets a merchant through a closing date
    // and keeps whatever date the order already had
    isSelectable: boolean
    // The one otherwise-unusable day that stays open because the order already had it.
    isGrandfathered: boolean
}>

// the API has shipped closing dates both as ISO and as "yyyy.MM.dd" — accept either
function normalizeClosingDate(value: string, dateTimeFactory: DateTimeFactory): string | null {
    const apiDate = dateTimeFactory.fromApi(value)
    const parsed = apiDate.isValid ? apiDate : DateTime.fromFormat(value, "yyyy.MM.dd")

    return parsed.isValid ? parsed.toISODate() : null
}

export function isClosingDate(date: DateTime, closingDates: string[], dateTimeFactory: DateTimeFactory): boolean {
    const normalizedClosingDates = new Set(
        closingDates
            .map((closingDate) => normalizeClosingDate(closingDate, dateTimeFactory))
            .filter((closingDate): closingDate is string => closingDate !== null),
    )
    const isoDate = date.toISODate()

    return !!isoDate && normalizedClosingDates.has(isoDate)
}

export function validateDeliveryDate(
    date: DateTime | null,
    { allowedWeekdays, closingDates, canOverrideClosingDates, grandfatheredDate }: DeliveryDateRules,
    dateTimeFactory: DateTimeFactory,
): DeliveryDateValidationResult {
    if (!date) {
        return {
            isAllowedWeekday: true,
            isNotClosed: true,
            isValidDeliveryDate: true,
            isSelectable: true,
            isGrandfathered: false,
        }
    }

    const isAllowedWeekday = allowedWeekdays.includes(date.weekday)
    const isNotClosed = !isClosingDate(date, closingDates, dateTimeFactory)
    const isValidDeliveryDate = isAllowedWeekday && isNotClosed
    // Outside the weekday check too: the shop may have dropped that weekday since the order.
    const isKeptFromOrder = !!grandfatheredDate && date.hasSame(grandfatheredDate, "day")

    return {
        isAllowedWeekday,
        isNotClosed,
        isValidDeliveryDate,
        isSelectable: isKeptFromOrder || (isAllowedWeekday && (isNotClosed || canOverrideClosingDates)),
        // Only worth calling out where it actually rescues an otherwise unusable day.
        isGrandfathered: isKeptFromOrder && !isValidDeliveryDate,
    }
}

export function isWithinOpeningHours(
    value: DateTime,
    deliveryDate: DateTime,
    openingHours: DeliveryTimeDay[],
    dateTimeFactory: DateTimeFactory,
): boolean {
    const limits = getOpeningLimitsForDate(deliveryDate, openingHours, dateTimeFactory)
    if (!limits) return true

    const valueTime = dateTimeFactory.toHourMinute(value)
    return (
        valueTime >= dateTimeFactory.toHourMinute(limits.minTime) &&
        valueTime <= dateTimeFactory.toHourMinute(limits.maxTime)
    )
}

export function isNotInPastIfToday(value: DateTime, deliveryDate: DateTime, dateTimeFactory: DateTimeFactory): boolean {
    if (!deliveryDate) return true
    const now = dateTimeFactory.now()
    const isToday = deliveryDate.hasSame(now, "day")
    if (!isToday) return true
    return value >= now
}

export function buildDeadlineDeliveryTime(
    deliveries: DeliveryTimesField | undefined,
    dateTimeFactory: DateTimeFactory,
): string {
    const deliveryDate = deliveries?.deliveryDate
    const firstTime = deliveries?.deliveryTimes[0]?.time
    if (!deliveryDate || !firstTime) return ""

    const deliveryDateTime = dateTimeFactory.fromISO(`${deliveryDate}T${firstTime}`)
    return deliveryDateTime.isValid ? dateTimeFactory.toApiISOMinutesFormat(deliveryDateTime) : ""
}

export function hasProductWishDelivery(deliveries: DeliveryTimesField | undefined): boolean {
    return deliveries?.deliveryTimes?.some((delivery) => delivery.orderLines?.some(isProductWishOrderLine)) ?? false
}

// product wishes and extra services carry no product deadline
function collectCartProductIds(deliveries: DeliveryTimesField | undefined): Set<number> {
    const ids = new Set<number>()

    for (const delivery of deliveries?.deliveryTimes ?? []) {
        for (const line of delivery.orderLines ?? []) {
            if (isProductWishOrderLine(line) || isLegacyExtraServiceOrderLine(line) || line.isExtraService) continue
            ids.add(line.id)
        }
    }

    return ids
}

// sorted so the query key doesn't change with insertion order
function collectDeadlineProductIds(deliveries: DeliveryTimesField | undefined): string {
    return Array.from(collectCartProductIds(deliveries))
        .sort((first, second) => first - second)
        .join(",")
}

export function isProductInCart(deliveries: DeliveryTimesField | undefined, productId: number): boolean {
    return collectCartProductIds(deliveries).has(productId)
}

type DeadlineMessageParamsArgs = Readonly<{
    priceList: SelectedPriceListItem
    deliveries: DeliveryTimesField | undefined
    dateTimeFactory: DateTimeFactory
    productFlow?: DeadlineMessageProductFlow
}>

// shared by the drawer card and the confirm step, so both hit one cache entry
export function buildDeadlineMessageParams({
    priceList,
    deliveries,
    dateTimeFactory,
    productFlow,
}: DeadlineMessageParamsArgs): DeadlineMessageParam {
    return {
        merchantId: priceList.merchantId,
        shopId: priceList.shopId,
        priceListId: priceList.priceListId,
        deliveryTime: buildDeadlineDeliveryTime(deliveries, dateTimeFactory),
        productWish: hasProductWishDelivery(deliveries),
        productIds: collectDeadlineProductIds(deliveries),
        productFlow,
    }
}
